import * as React from 'react';
import { useContext } from 'react';
import TripContext from '../../context/trip/tripContext';
import DeleteIcon from '@mui/icons-material/Delete';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import Link from '@mui/joy/Link';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import IconButton from '@mui/joy/IconButton';
import AspectRatio from '@mui/joy/AspectRatio';
import Chip from '@mui/joy/Chip';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';

const TripItem = ({ trip, showButton, showDelete, fromPath }) => {
    const { deleteTrip } = useContext(TripContext);
    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (window.confirm('Delete this trip?')) {
            deleteTrip(trip.id);
        }
    };
    const {
        photos,
        title,
        content,
        avatar,
        owner_first_name,
        owner_last_name,
        spot_reviews,
    } = trip;

    // Try trip photos first, then walk spot reviews (skipping deleted/photo-less
    // ones) for the first available image, then fallback.
    const firstReviewPhoto = (spot_reviews || [])
        .filter((r) => !r.deleted && r.photos && r.photos[0] && r.photos[0].image_link)
        .map((r) => r.photos[0].image_link)[0];
    const coverPhoto =
        (photos && photos.length > 0 && photos[0].image_link) ||
        firstReviewPhoto ||
        null;
    const isDraft = trip.visibility === 'draft';

    const reviewCount = spot_reviews ? spot_reviews.length : 0;

    return (
        <Card
            orientation="horizontal"
            variant="outlined"
            sx={{
                width: '100%',
                p: 0,
                overflow: 'hidden',
                position: 'relative',
                transition: 'transform 200ms ease, box-shadow 200ms ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                },
            }}
        >
            <CardOverflow>
                <AspectRatio ratio="1" sx={{ width: { xs: 140, sm: 180 } }}>
                    {coverPhoto ? (
                        <img src={coverPhoto} loading="lazy" alt={title} />
                    ) : (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: isDraft ? 'neutral.100' : 'primary.50',
                            width: '100%', height: '100%',
                        }}>
                            {isDraft
                                ? <EditNoteIcon sx={{ fontSize: 48, color: 'neutral.400' }} />
                                : <FlightTakeoffIcon sx={{ fontSize: 48, color: 'primary.300' }} />}
                        </Box>
                    )}
                </AspectRatio>
            </CardOverflow>
            <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography level="title-lg" sx={{ fontWeight: 600 }}>
                        {showButton ? (
                            <Link
                                overlay
                                underline="none"
                                href={`/trip/${trip.id}${fromPath ? `?from=${encodeURIComponent(fromPath)}` : ''}`}
                                sx={{ color: 'inherit' }}
                            >
                                {title}
                            </Link>
                        ) : title}
                    </Typography>
                    {trip.visibility === 'draft' && (
                        <Chip size="sm" variant="soft" color="neutral" sx={{ fontWeight: 600 }}>Draft</Chip>
                    )}
                </Box>
                <Typography
                    level="body-sm"
                    sx={{
                        color: 'text.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mt: 0.5,
                    }}
                >
                    {content}
                </Typography>

                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    {avatar && (
                        <Avatar
                            src={avatar.image_link}
                            size="sm"
                            sx={{ '--Avatar-size': '1.5rem' }}
                        />
                    )}
                    {(trip.owner_name || owner_first_name) && (
                        <Typography level="body-xs" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {trip.owner_name || (owner_first_name + " " + owner_last_name)}
                        </Typography>
                    )}
                    {reviewCount > 0 && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', ml: 'auto' }}>
                            {reviewCount} {reviewCount === 1 ? 'activity' : 'activities'}
                        </Typography>
                    )}
                </Box>
            </CardContent>

            {/* Delete — pinned to the top-right corner so it stays visible at
                narrow widths. Absolute so the CardContent title/content can
                flow the full remaining width without being squeezed by a
                flex child. zIndex sits above the overlay Link. */}
            {showDelete && (
                <IconButton
                    variant="plain"
                    color="danger"
                    size="sm"
                    onClick={handleDelete}
                    aria-label="Delete trip"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                    }}
                >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
            )}
        </Card>
    );
}

export default TripItem;
